import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useVacationRequestContext } from '../../contexts/VacationRequestProvider';
import useVacationRequestActions from '../../hooks/APIHooks/users/useVacationRequestActions';
import { VacationRequest } from '../../types/vacations';
import { fetchErrorMessage } from '../../utils/fetchErrorMessage';
import ConsentModal from '../CommonComps/Modals/ConsentModal';
import CreateEditVacationRequestModal from '../CommonComps/Modals/CreateEditVacationRequestModal';
import VacationRequestRejectModal from '../CommonComps/Modals/VacationRequestRejectModal';

const VacationMutationModals = () => {
  // integration of react hooks here
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isApproving, setIsApproving] = useState<boolean>(false);

  // integration of context hooks here
  const {
    showUpdateModal,
    showDeleteModal,
    showApproveModal,
    showRejectModal,
    vacationRequest,
    setShowUpdateModal,
    setShowDeleteModal,
    setShowApproveModal,
    setShowRejectModal,
  } = useVacationRequestContext();

  // integration of custom API hooks here
  const { deleteVacationRequestByIdHandler, approveOrRejectRequestHandler } =
    useVacationRequestActions('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // handler function to handle delete vacation request declared here
  const deleteVacationRequestHandler = async () => {
    try {
      setIsDeleting(true);

      await deleteVacationRequestByIdHandler
        .mutateAsync({ vacReqId: vacationRequest?.id as number })
        .then((res) => {
          if (res.status === 204) {
            toast.success('Request Deleted!!');

            setShowDeleteModal(false);
            queryClient.invalidateQueries({
              queryKey: ['vacation-request-list'],
            });
          } else {
            toast.error(res.data.message || 'Request Deletion Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      setIsDeleting(false);
    }
  };

  const approveVacationRequestHandler = async () => {
    try {
      setIsApproving(true);

      await approveOrRejectRequestHandler
        .mutateAsync({
          vacReqId: vacationRequest?.id as number,
          body: { is_approved: true },
        })
        .then((res) => {
          console.log(res);
          if (res.is_success) {
            toast.success('Request Approved!!');

            setShowApproveModal(false);
            queryClient.invalidateQueries({
              queryKey: ['vacation-request-list'],
            });
          } else {
            toast.error(res.message || 'Request Approving Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      setIsApproving(false);
    }
  };

  // rendering vacation mutation modals section component here
  return (
    <section>
      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showUpdateModal ? (
          <CreateEditVacationRequestModal
            title='update'
            vacationRequest={vacationRequest as VacationRequest}
            handleModalClose={() => setShowUpdateModal(false)}
          />
        ) : null}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showDeleteModal ? (
          <ConsentModal
            titleText='Are you sure?'
            subTitle={
              <>
                Are you sure you want to delete this vacation request? Once you
                delete it, you can not recover it.
              </>
            }
            isLoading={isDeleting}
            yesBtnTxt='Yes'
            yesBtnVariant='danger'
            noBtnTxt='No'
            noBtnVariant='success'
            mutationHandler={deleteVacationRequestHandler}
            handleModalClose={() => setShowDeleteModal(false)}
          />
        ) : null}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showApproveModal ? (
          <ConsentModal
            titleText='Are you sure?'
            subTitle={
              <>Are you sure you want to approve this vacation request?</>
            }
            isLoading={isApproving}
            yesBtnTxt='Yes'
            yesBtnVariant='success'
            noBtnTxt='No'
            noBtnVariant='danger'
            mutationHandler={approveVacationRequestHandler}
            handleModalClose={() => setShowApproveModal(false)}
          />
        ) : null}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showRejectModal ? (
          <VacationRequestRejectModal
            vacationRequestId={vacationRequest?.id as number}
            handleModalClose={() => setShowRejectModal(false)}
          />
        ) : null}
      </AnimatePresence>
    </section>
  );
};

export default VacationMutationModals;
